import { isPremium, comparisonLimit, registrationRaisesLimit, examDiffProUrl } from './common';
import { canUseExamDiff } from './browser';

export function setUpComparisonCounter(onSetComparisonCount, configuration) {
  let comparisonCount = 0;
  let comparisonStartDate = undefined;

  try {
    comparisonCount = JSON.parse(window.localStorage.getItem('diffNowComparisonCount'));
    comparisonStartDate = new Date(window.localStorage.getItem('diffNowComparisonStartDate'));
  } catch (e) {
    // Fall back to default values
  }

  if (comparisonCount && comparisonStartDate) {
    if (isComparisonCountExpired(comparisonStartDate, configuration)) {
      resetComparisonCounter(onSetComparisonCount);
    } else {
      onSetComparisonCount(comparisonCount, comparisonStartDate);
    }
  } else {
    resetComparisonCounter(onSetComparisonCount);
  }
}

export function resetComparisonCounter(onSetComparisonCount) {
  setComparisonCount(0, null, onSetComparisonCount);
}

export function bumpComparisonCounter({
  comparisonCount,
  comparisonStartDate,
  onSetComparisonCount,
  configuration,
  user
}) {
  if (!isPremium(user)) {
    if (comparisonCount && comparisonStartDate) {
      if (isComparisonCountExpired(comparisonStartDate, configuration)) {
        setComparisonCount(1, new Date(), onSetComparisonCount);
      } else {
        setComparisonCount(comparisonCount + 1, comparisonStartDate, onSetComparisonCount);
      }
    } else {
      setComparisonCount(1, new Date(), onSetComparisonCount);
    }
  }
}

export function setComparisonCount(
  newComparisonCount,
  newComparisonStartDate,
  onSetComparisonCount
) {
  onSetComparisonCount(newComparisonCount, newComparisonStartDate);

  try {
    window.localStorage.setItem('diffNowComparisonCount', newComparisonCount);
    window.localStorage.setItem('diffNowComparisonStartDate', newComparisonStartDate);
  } catch (e) {
    // Fall back to default values
  }
}

export function isComparisonCountExpired(comparisonStartDate, configuration) {
  const { useMonthlyComparisonLimits } = configuration;
  const MS_IN_A_DAY = 86400000;
  const MS_IN_A_MONTH = 30 * MS_IN_A_DAY;

  const expirationLimit = useMonthlyComparisonLimits ? MS_IN_A_MONTH : MS_IN_A_DAY;

  const currentTime = new Date();
  return currentTime - comparisonStartDate > expirationLimit;
}

export function comparisonsUsedStatus(
  comparisonCount,
  user,
  configuration,
  isAdblockEnabled = false
) {
  const {
    comparisonCountLimitNotLoggedInWarning,
    comparisonCountLimitNotLoggedInMax,
    comparisonCountLimitLoggedInWarning,
    comparisonCountLimitLoggedInMax,
    monthlyComparisonCountLimitWarning,
    monthlyComparisonCountLimitMax,
    monthlyComparisonCountLimitLoggedInWarning,
    monthlyComparisonCountLimitLoggedInMax,
    comparisonCountLimitAdblockWarning,
    comparisonCountLimitAdblockMax,
    monthlyComparisonCountLimitAdblockWarning,
    monthlyComparisonCountLimitAdblockMax,
    useMonthlyComparisonLimits
  } = configuration;

  const limitTypeDescriptor = useMonthlyComparisonLimits ? 'monthly' : 'daily';
  const userNotLoggedInOrNotPremium = !user || !isPremium(user);

  let warningLimit, maxLimit, message;
  if (isAdblockEnabled && userNotLoggedInOrNotPremium) {
    warningLimit = useMonthlyComparisonLimits
      ? monthlyComparisonCountLimitAdblockWarning
      : comparisonCountLimitAdblockWarning;
    maxLimit = useMonthlyComparisonLimits
      ? monthlyComparisonCountLimitAdblockMax
      : comparisonCountLimitAdblockMax;
    message = `You have used ${comparisonCount} of your ${maxLimit} ${limitTypeDescriptor} comparisons. DiffNow is ad-supported — disable your ad blocker to increase this limit, or upgrade to <OpenWhyPremium source="comparison-count">DiffNow Premium</OpenWhyPremium> for unlimited comparisons.`;
  } else {
    warningLimit = useMonthlyComparisonLimits
      ? user
        ? monthlyComparisonCountLimitLoggedInWarning
        : monthlyComparisonCountLimitWarning
      : user
      ? comparisonCountLimitLoggedInWarning
      : comparisonCountLimitNotLoggedInWarning;
    maxLimit = useMonthlyComparisonLimits
      ? user
        ? monthlyComparisonCountLimitLoggedInMax
        : monthlyComparisonCountLimitMax
      : user
      ? comparisonCountLimitLoggedInMax
      : comparisonCountLimitNotLoggedInMax;
    message = `You have used ${comparisonCount} of your ${maxLimit} ${limitTypeDescriptor} comparisons. <OpenWhyPremium source="comparison-count">Premium users</OpenWhyPremium> can make an unlimited number of comparisons.`;
  }

  if (userNotLoggedInOrNotPremium && comparisonCount >= warningLimit) {
    return { message, type: 'info' };
  } else {
    return { message: null, type: null };
  }
}

export function comparisonLimitReachedStatus(
  user,
  configuration,
  isAdblockEnabled = false,
  isWindows = canUseExamDiff()
) {
  const limitTypeDescriptor = configuration.useMonthlyComparisonLimits ? 'monthly' : 'daily';
  const limit = comparisonLimit(user, configuration, isAdblockEnabled);

  if (isAdblockEnabled && !isPremium(user)) {
    return {
      message: `You have hit your ${limitTypeDescriptor} comparison limit of ${limit}. DiffNow is ad-supported — disable your ad blocker to increase this limit, or sign up for <OpenWhyPremium source="comparison-count">DiffNow Premium</OpenWhyPremium> to unlock unlimited comparisons.`,
      type: 'error'
    };
  }

  let additionalMessage;
  if (!user && registrationRaisesLimit(configuration)) {
    additionalMessage = `Register for free to increase your comparison limit to ${comparisonLimit(
      { isPremium: false },
      configuration
    )} or sign up for <OpenWhyPremium source="comparison-count">DiffNow Premium</OpenWhyPremium> to unlock unlimited comparisons.`;
  } else if (isWindows) {
    additionalMessage = `For unlimited comparisons on Windows, try <a href="${examDiffProUrl(
      'diffnow-limit'
    )}">ExamDiff Pro</a>, our desktop app (free 30-day trial), or sign up for <OpenWhyPremium source="comparison-count">DiffNow Premium</OpenWhyPremium>.`;
  } else {
    additionalMessage =
      'Sign up for <OpenWhyPremium source="comparison-count">DiffNow Premium</OpenWhyPremium> to unlock unlimited comparisons.';
  }

  return {
    message: `You have hit your ${limitTypeDescriptor} comparison limit of ${limit}. ${additionalMessage}`,
    type: 'error'
  };
}
